
import React, { useEffect, useRef, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { College } from '@/hooks/useCollegeSearch';
import CollegeCard from './CollegeCard';
import CollegeList from './CollegeList';
import { Grid, List, Loader2 } from 'lucide-react';

interface CollegeSearchDisplayProps {
  colleges: (College & { isSaved?: boolean; savedStatus?: string; savedNotes?: string })[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onToggleSave: (collegeId: string) => void;
  showSavedDetails?: boolean;
  onCollegeClick?: (college: College) => void;
  onSuggestCorrection?: (collegeId: string) => void;
}

const CollegeSearchDisplay: React.FC<CollegeSearchDisplayProps> = ({
  colleges,
  loading,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onToggleSave,
  showSavedDetails = false,
  onCollegeClick,
  onSuggestCorrection
}) => {
  const [view, setView] = React.useState<'grid' | 'list'>('grid');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, loadingMore, onLoadMore]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  if (loading && colleges.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-64 bg-gray-100 rounded-md animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (colleges.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <h3 className="text-lg font-medium mb-2">No colleges found</h3>
        <p className="text-gray-500">Try adjusting your search filters.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end items-center mb-4">
        <div className="flex space-x-2">
          <TabsList className="grid w-20 grid-cols-2">
            <TabsTrigger 
              value="grid" 
              onClick={() => setView('grid')}
              className={view === 'grid' ? 'data-[state=active]:bg-gray-100' : ''}
            >
              <Grid className="h-4 w-4" />
            </TabsTrigger>
            <TabsTrigger 
              value="list" 
              onClick={() => setView('list')}
              className={view === 'list' ? 'data-[state=active]:bg-gray-100' : ''}
            >
              <List className="h-4 w-4" />
            </TabsTrigger>
          </TabsList>
        </div>
      </div>

      <Tabs defaultValue="grid" value={view}>
        <TabsContent value="grid" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {colleges.map((college) => (
              <CollegeCard
                key={college.id}
                college={college}
                onClick={() => onCollegeClick && onCollegeClick(college)}
                onToggleSave={onToggleSave}
                onSuggestCorrection={onSuggestCorrection}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-0">
          <CollegeList
            colleges={colleges}
            loading={loading}
            onToggleSave={onToggleSave}
            showSavedDetails={showSavedDetails}
            onCollegeClick={onCollegeClick}
            onSuggestCorrection={onSuggestCorrection}
          />
        </TabsContent>
      </Tabs>
      
      {/* Infinite scroll trigger */}
      <div ref={loadMoreRef} className="mt-6 flex justify-center min-h-[40px]">
        {loadingMore && (
          <div className="flex items-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading more colleges...
          </div>
        )}
        {!hasMore && colleges.length > 0 && (
          <p className="text-sm text-muted-foreground">All colleges loaded</p>
        )}
      </div>
    </div>
  );
};

export default CollegeSearchDisplay;
